import { Button, Group, Select, Paper, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useSamplingQueries } from '../../data-access/queries/sampling';

import { TransferList } from '@ui';

export interface CreateOrderFormValues {
  farm: string;
  analysis_type: string;
  sampler: string;
  laboratory: string;
  points: string[];
}
const schema = z.object({
  farm: z.string(),
  analysis_type: z.string(),
  sampler: z.string(),
  laboratory: z.string(),
  points: z.array(z.string()),
});

export function CreateOrderForm({
  onSubmit,
  onCancel,
}: {
  onSubmit?: (values: CreateOrderFormValues) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const form = useForm<CreateOrderFormValues>({
    validate: {
      farm: (value) => (!value ? t('forms.required') : null),
      analysis_type: (value) => (!value ? t('forms.required') : null),
      sampler: (value) => (!value ? t('forms.required') : null),
      laboratory: (value) => (!value ? t('forms.required') : null),
      points: (value) => (value.length === 0 ? t('forms.required') : null),
    },
  });
  const [points, setPoints] = useState<
    {
      value: string;
      label: string;
    }[]
  >([]);

  const samplingQueries = useSamplingQueries();
  const filters = schema.omit({ points: true }).partial().parse(form.values);
  const formValues = useQuery({
    ...samplingQueries.orders.values(filters),
    placeholderData: keepPreviousData,
  });

  const handleSubmit = (values: CreateOrderFormValues) => {
    if (onSubmit) onSubmit(values);
    // Add mutation logic here if needed
  };

  form.watch('points', ({ value }) => {
    setPoints(
      formValues.data?.points
        .filter((point) => value.includes(point.id.toString()))
        .map((point) => ({
          value: point.id.toString(),
          label: point.code,
        })) || [],
    );
  });

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md" mt="sm">
        <Select
          data={formValues.data?.farm.map(({ id, name }) => ({
            value: id.toString(),
            label: name,
          }))}
          label={t('common.societe')}
          required
          {...form.getInputProps('farm')}
        />
        <Select
          data={formValues.data?.analysis_type}
          label={t('common.type-danalyse')}
          required
          {...form.getInputProps('analysis_type')}
        />
        <Select
          data={formValues.data?.sampler.map(
            ({ id, last_name, first_name }) => ({
              value: id.toString(),
              label: `${first_name} ${last_name}`,
            }),
          )}
          label={t('common.preleveur')}
          required
          {...form.getInputProps('sampler')}
        />
        <Select
          data={formValues.data?.laboratory.map(({ id, name }) => ({
            value: id.toString(),
            label: name,
          }))}
          label={t('sampling.laboratoire')}
          required
          {...form.getInputProps('laboratory')}
        />

        <TransferList
          availableOptions={
            formValues.data?.points.map(({ id, code }) => ({
              value: id.toString(),
              label: code,
            })) || []
          }
          initialSelected={points}
          leftTitle={t('common.points-disponibles')}
          onChange={(pts) => {
            form.setFieldValue('points', pts);
          }}
          rightTitle={t('common.points-selectionnes')}
          searchable
        />

        <Group mt="md">
          <Button type="submit">{t('common.generate')}</Button>
          <Button color="red" onClick={onCancel} type="button">
            {t('common.cancel')}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
